import React, { useEffect } from 'react';
import { DetailedIncidentCard } from '@pages/incidents/components/DetailedIncidentCard';
import { Loading } from '@components/atoms/Loading';
import ErrorMessage from '@components/molecules/ErrorMessage';
import { Button } from '@components/atoms/Button';
import { useIncidentsByProject } from '@hooks/api/incident/useIncidentsByProject';
import { useTranslation } from '@hooks/useTranslation';
import { Incident, IncidentStatus } from '@models/incident.model';

interface ClientIncidentsTabProps {
  projectId: string;
  onCountChange?: (count: number) => void;
}

/**
 * Sprint 43, story-224. Read-only incident list for a CLIENT whose checklist has `incidents` ON.
 * Reuses `DetailedIncidentCard` with no `onClick`: there is no acknowledge, resolve, report or
 * edit control anywhere in this tab. Newest first (the server's order). Mounted only while its
 * tab is selected.
 */
export const ClientIncidentsTab: React.FC<ClientIncidentsTabProps> = ({
  projectId,
  onCountChange,
}) => {
  const tProjects = useTranslation('projects');
  const tIncidents = useTranslation('incidents');
  const tActivityCards = useTranslation('activityCards');
  const tCommon = useTranslation('common');
  const {
    data: incidents = [],
    isLoading,
    isError,
    refetch,
  } = useIncidentsByProject(projectId);

  useEffect(() => {
    if (!isLoading && !isError) onCountChange?.(incidents.length);
  }, [isLoading, isError, incidents.length, onCountChange]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Loading size="xl" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col gap-3">
        <ErrorMessage message={tProjects.clientTabLoadError} />
        <div>
          <Button variant="tertiary" onClick={() => refetch()}>
            {tCommon.retry}
          </Button>
        </div>
      </div>
    );
  }

  if (incidents.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
        {tProjects.clientIncidentsEmpty}
      </p>
    );
  }

  const typeText: Record<string, string> = {
    WEATHER: tIncidents.typeWeather,
    ACCIDENT: tIncidents.typeAccident,
    MATERIALS_ISSUE: tIncidents.typeMaterialsIssue,
    EQUIPMENT_ISSUE: tIncidents.typeEquipmentIssue,
    OTHER: tIncidents.typeOther,
  };
  const unitText: Record<string, string> = {
    HOURS: tIncidents.unitHours,
    DAYS: tIncidents.unitDays,
    WEEKS: tIncidents.unitWeeks,
  };

  const typeLabel = (incident: Incident): string => {
    if (incident.incidentType === 'OTHER' && incident.otherIncidentType) {
      return incident.otherIncidentType;
    }
    return (
      (incident.incidentType && typeText[incident.incidentType]) ||
      tActivityCards.incident.unknownTypeFallback
    );
  };
  const delayLabel = (incident: Incident): string =>
    incident.delay
      ? `${incident.delay} ${
          (incident.delayUnitOfMeasure && unitText[incident.delayUnitOfMeasure]) ||
          tIncidents.unitHours
        }`
      : 'N/A';
  const statusLabel = (incident: Incident): string =>
    incident.status === IncidentStatus.ACKNOWLEDGED
      ? tActivityCards.incident.statusAcknowledged
      : tActivityCards.incident.statusPending;
  const reporter = (incident: Incident): string | undefined => {
    const name = `${incident.user?.firstName || ''} ${incident.user?.lastName || ''}`.trim();
    return name || undefined;
  };

  return (
    <div className="space-y-4">
      {incidents.map((incident) => (
        <DetailedIncidentCard
          key={incident.incidentId}
          name={incident.name}
          status={statusLabel(incident)}
          additionalInfo={incident.description || ''}
          date={new Date(incident.incidentTime || incident.createdAt).toLocaleString(
            'en-US',
            {
              month: 'long',
              day: 'numeric',
              year: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
            },
          )}
          type={typeLabel(incident)}
          delay={delayLabel(incident)}
          projectName={incident.project?.name}
          reportedBy={reporter(incident)}
          documentsCount={incident.documents?.length}
        />
      ))}
    </div>
  );
};

export default ClientIncidentsTab;
